import { cva } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import {
  CalendarDays,
  Check,
  CheckCircle,
  Clock,
  FileText,
  OctagonAlertIcon,
  PhoneOff,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  type ChipStatus,
  ChipStatusEnum,
  type ContactabilityFilter,
  type ContactabilityFilterCount,
  ContactabilityFilterEnum,
  ContactabilityFilterLabel,
  type FilterChipCount,
  type UrgencyFilter,
  type UrgencyFilterCount,
  UrgencyFilterEnum,
  UrgencyFilterLabel,
} from '@/models/ui/filterChip';

interface FilterChipProps {
  readonly filterChipCount?: FilterChipCount;
  readonly urgencyFilterCount?: UrgencyFilterCount;
  readonly contactabilityFilterCount?: ContactabilityFilterCount;
  readonly isActive: boolean;
  readonly onToggle?: (status: ChipStatus) => void;
  readonly onToggleUrgency?: (urgency: UrgencyFilter) => void;
  readonly onToggleContactability?: (
    contactability: ContactabilityFilter,
  ) => void;
}

const chipVariants = cva(
  'flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold transition-all border shadow-sm',
  {
    variants: {
      status: {
        Lead: 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100',
        Scheduled: 'bg-blue-50 border-blue-100 text-blue-700 hover:bg-blue-100',
        'In Repair':
          'bg-indigo-50 border-indigo-100 text-indigo-700 hover:bg-indigo-100',
        Executed:
          'bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-100',
      },
      active: {
        true: '!bg-[#4A4741] !text-white !border-[#4A4741] ring-1 ring-[#4A4741] ring-offset-1',
        false: '',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

const iconVariants = cva('w-3.5 h-3.5 transition-colors', {
  variants: {
    status: {
      Lead: 'text-slate-500',
      Scheduled: 'text-blue-600',
      'In Repair': 'text-indigo-600',
      Executed: 'text-emerald-600',
    },
    active: {
      true: '!text-white',
      false: '',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const statusIcons: Record<ChipStatus, LucideIcon> = {
  [ChipStatusEnum.enum.Lead]: FileText,
  [ChipStatusEnum.enum.Scheduled]: CalendarDays,
  [ChipStatusEnum.enum['In Repair']]: Wrench,
  [ChipStatusEnum.enum.Executed]: CheckCircle,
};

const urgencyChipVariants = cva(
  'flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold transition-all border shadow-sm',
  {
    variants: {
      urgency: {
        Overdue: 'bg-red-50 border-red-100 text-red-700 hover:bg-red-100',
        'Overdue Soon':
          'bg-amber-50 border-amber-100 text-amber-700 hover:bg-amber-100',
      },
      active: {
        true: '!bg-[#4A4741] !text-white !border-[#4A4741] ring-1 ring-[#4A4741] ring-offset-1',
        false: '',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

const urgencyIconVariants = cva('w-3.5 h-3.5 transition-colors', {
  variants: {
    urgency: {
      Overdue: 'text-red-600',
      'Overdue Soon': 'text-amber-600',
    },
    active: {
      true: '!text-white',
      false: '',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const urgencyIcons: Record<UrgencyFilter, LucideIcon> = {
  [UrgencyFilterEnum.enum.Overdue]: Clock,
  [UrgencyFilterEnum.enum.OverdueSoon]: OctagonAlertIcon,
};

const contactabilityChipVariants = cva(
  'flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold transition-all border shadow-sm',
  {
    variants: {
      contactability: {
        Unreachable:
          'bg-zinc-100 border-zinc-200 text-zinc-700 hover:bg-zinc-200',
      },
      active: {
        true: '!bg-[#4A4741] !text-white !border-[#4A4741] ring-1 ring-[#4A4741] ring-offset-1',
        false: '',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

const contactabilityIconVariants = cva('w-3.5 h-3.5 transition-colors', {
  variants: {
    contactability: {
      Unreachable: 'text-zinc-600',
    },
    active: {
      true: '!text-white',
      false: '',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const contactabilityIcons: Record<ContactabilityFilter, LucideIcon> = {
  [ContactabilityFilterEnum.enum.Unreachable]: PhoneOff,
};

function formatLabel(status: string) {
  return status
    .replaceAll(/([A-Z])/g, ' $1')
    .trim()
    .toUpperCase();
}

export function FilterChip({
  filterChipCount,
  urgencyFilterCount,
  contactabilityFilterCount,
  isActive,
  onToggle,
  onToggleUrgency,
  onToggleContactability,
}: FilterChipProps) {
  if (!filterChipCount && !urgencyFilterCount && !contactabilityFilterCount) {
    return null;
  }

  const countBadgeClass = cn(
    'flex items-center justify-center min-w-[1.25rem] h-4 rounded-full text-[9px] font-bold px-1 ml-auto transition-colors',
    isActive ? 'bg-white/20 text-white' : 'bg-black/5 text-current/60',
  );

  if (contactabilityFilterCount) {
    const contactability = contactabilityFilterCount.contactability;
    const Icon = isActive ? Check : contactabilityIcons[contactability];
    return (
      <li className="list-none">
        <button
          type="button"
          onClick={() => onToggleContactability?.(contactability)}
          className={contactabilityChipVariants({
            contactability,
            active: isActive,
          })}
          aria-pressed={isActive}
          aria-label={ContactabilityFilterLabel[contactability]}
        >
          <Icon
            className={contactabilityIconVariants({
              contactability,
              active: isActive,
            })}
          />
          <span className="whitespace-nowrap tracking-tight leading-none">
            {ContactabilityFilterLabel[contactability]}
          </span>
          <span className={countBadgeClass}>
            {contactabilityFilterCount.count}
          </span>
        </button>
      </li>
    );
  }

  if (urgencyFilterCount) {
    const urgency = urgencyFilterCount.urgency;
    const Icon = isActive ? Check : urgencyIcons[urgency];
    return (
      <li className="list-none">
        <button
          type="button"
          onClick={() => onToggleUrgency?.(urgency)}
          className={urgencyChipVariants({ urgency, active: isActive })}
          aria-pressed={isActive}
          aria-label={UrgencyFilterLabel[urgency]}
        >
          <Icon
            className={urgencyIconVariants({ urgency, active: isActive })}
          />
          <span className="whitespace-nowrap tracking-tight leading-none">
            {UrgencyFilterLabel[urgency]}
          </span>
          <span className={countBadgeClass}>{urgencyFilterCount.count}</span>
        </button>
      </li>
    );
  }

  if (!filterChipCount) {
    return null;
  }

  const status = ChipStatusEnum.parse(filterChipCount.status);
  const Icon = isActive ? Check : statusIcons[status];

  return (
    <li className="list-none">
      <button
        type="button"
        onClick={() => onToggle?.(status)}
        className={chipVariants({ status, active: isActive })}
        aria-pressed={isActive}
        aria-label={status}
      >
        <Icon className={iconVariants({ status, active: isActive })} />
        <span className="whitespace-nowrap tracking-tight leading-none">
          {formatLabel(status)}
        </span>
        <span className={countBadgeClass}>{filterChipCount.count}</span>
      </button>
    </li>
  );
}
